import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Colleague } from '@/types/types';
import { colleagueService } from '@/services/colleaguesService';

interface ColleagueContextType {
  colleagues: Colleague[];
  loading: boolean;
  error: string | null;
  refetchColleagues: () => Promise<void>;
}

const ColleagueContext = createContext<ColleagueContextType | undefined>(undefined);

export const useColleagues = () => {
  const context = useContext(ColleagueContext);
  if (context === undefined) {
    throw new Error('useColleagues must be used within a ColleagueProvider');
  }
  return context;
};

interface ColleagueProviderProps {
  children: ReactNode;
}

export const ColleagueProvider: React.FC<ColleagueProviderProps> = ({ children }) => {
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchColleagues = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await colleagueService.getAllColleagues();
      setColleagues(data);
    } catch (err) {
      console.error('Error fetching colleagues:', err);
      setError('Failed to load colleagues. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchColleagues();
  }, []);

  const refetchColleagues = async () => {
    await fetchColleagues();
  };

  return (
    <ColleagueContext.Provider value={{ colleagues, loading, error, refetchColleagues }}>
      {children}
    </ColleagueContext.Provider>
  );
};